/**
 * Text Analysis Utilities
 *
 * Shared utilities for analyzing text structure, used by the Structure Analyzer
 * and other text processing features.
 */

/**
 * Get word count from text
 */
export function getWordCount(text: string): number {
  if (!text.trim()) return 0
  return text.trim().split(/\s+/).filter(Boolean).length
}

/**
 * Split text into paragraphs (separated by blank lines or double newlines)
 */
export function getParagraphs(text: string): string[] {
  if (!text.trim()) return []
  return text
    .split(/\n\s*\n/)
    .map(p => p.trim())
    .filter(Boolean)
}

/**
 * Split text into sentences
 * Handles common sentence endings: . ! ? and accounts for abbreviations
 */
export function getSentences(text: string): string[] {
  if (!text.trim()) return []

  // Split on sentence-ending punctuation followed by space or end of string
  // Negative lookbehind for common abbreviations (Mr., Mrs., Dr., etc.)
  const sentences = text
    .replace(/([.!?])\s+/g, '$1|SPLIT|')
    .split('|SPLIT|')
    .map(s => s.trim())
    .filter(Boolean)

  return sentences
}

/**
 * Normalize text for analysis
 * - Lowercase
 * - Remove extra whitespace
 * - Normalize quotes and apostrophes
 */
export function getNormalizedText(text: string): string {
  return text
    .toLowerCase()
    .replace(/[\u2018\u2019]/g, "'")  // Smart single quotes
    .replace(/[\u201C\u201D]/g, '"')  // Smart double quotes
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Get position category based on character index
 */
export function getTextPosition(
  index: number,
  length: number
): 'beginning' | 'middle' | 'end' {
  if (length === 0) return 'beginning'
  const percentile = (index / length) * 100

  if (percentile <= 30) return 'beginning'
  if (percentile >= 70) return 'end'
  return 'middle'
}

/**
 * Get percentile position (0-100) of an index within text
 */
export function getPercentilePosition(index: number, length: number): number {
  if (length === 0) return 0
  return Math.round((index / length) * 100)
}

/**
 * Find all occurrences of a pattern in text
 * Returns array of { index, match, percentile }
 */
export function findPatternOccurrences(
  text: string,
  pattern: RegExp
): Array<{ index: number; match: string; percentile: number }> {
  const results: Array<{ index: number; match: string; percentile: number }> = []
  const normalizedText = getNormalizedText(text)

  let match: RegExpExecArray | null
  const globalPattern = new RegExp(pattern.source, 'gi')

  while ((match = globalPattern.exec(normalizedText)) !== null) {
    results.push({
      index: match.index,
      match: match[0],
      percentile: getPercentilePosition(match.index, normalizedText.length),
    })
  }

  return results
}

/**
 * Extract a snippet of text around a given index
 * Useful for showing context in analysis notes
 */
export function extractSnippet(
  text: string,
  index: number,
  contextLength: number = 50
): string {
  const start = Math.max(0, index - contextLength)
  const end = Math.min(text.length, index + contextLength)

  let snippet = text.slice(start, end).trim()

  // Add ellipsis if truncated
  if (start > 0) snippet = '...' + snippet
  if (end < text.length) snippet = snippet + '...'

  return snippet
}

/**
 * Check if text contains any of the given keywords
 * Returns the first match found with its position
 */
export function findFirstKeyword(
  text: string,
  keywords: string[]
): { keyword: string; index: number; percentile: number } | null {
  const normalizedText = getNormalizedText(text)

  for (const keyword of keywords) {
    // Create word-boundary pattern to avoid partial matches
    const pattern = new RegExp(`\\b${escapeRegExp(keyword)}\\b`, 'i')
    const match = normalizedText.match(pattern)

    if (match && match.index !== undefined) {
      return {
        keyword: match[0],
        index: match.index,
        percentile: getPercentilePosition(match.index, normalizedText.length),
      }
    }
  }

  return null
}

/**
 * Find all keywords in text with their positions
 */
export function findAllKeywords(
  text: string,
  keywords: string[]
): Array<{ keyword: string; index: number; percentile: number }> {
  const normalizedText = getNormalizedText(text)
  const results: Array<{ keyword: string; index: number; percentile: number }> = []

  for (const keyword of keywords) {
    const pattern = new RegExp(`\\b${escapeRegExp(keyword)}\\b`, 'gi')
    let match: RegExpExecArray | null

    while ((match = pattern.exec(normalizedText)) !== null) {
      results.push({
        keyword: match[0],
        index: match.index,
        percentile: getPercentilePosition(match.index, normalizedText.length),
      })
    }
  }

  // Sort by position
  return results.sort((a, b) => a.index - b.index)
}

/**
 * Escape special regex characters in a string
 */
function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}
